import { useState } from 'react';
import { RefreshCw, Database, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';

/**
 * DevTools Page
 * 
 * ⚠️ NOTE: This page uses Electron-specific admin APIs and is not functional in the web app.
 * Admin/developer tools should be accessed through the desktop application.
 */
export default function DevTools() {
  const [stats, setStats] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [fixed, setFixed] = useState(null);
  const [error, setError] = useState(null);

  const isElectron = typeof window !== 'undefined' && window.electron;

  const loadStats = async () => {
    if (!isElectron) {
      setError('Admin tools are only available in the desktop app');
      return;
    }
    try {
      const data = await window.electron.invoke('admin:getStats');
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const fixOrphanedData = async () => {
    if (!isElectron) {
      setError('Admin tools are only available in the desktop app');
      return;
    }
    setFixing(true);
    setError(null);
    try {
      const result = await window.electron.invoke('admin:fixOrphanedData');
      setFixed(result);
      await loadStats(); // Reload stats
    } catch (err) {
      setError(err.message);
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">Developer Tools</h1>
        <p className="text-sm text-slate-600">
          Troubleshooting and database utilities
        </p>
      </div>

      {/* Web App Warning */}
      {!isElectron && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">Desktop App Only</p>
            <p className="text-sm text-amber-700 mt-1">
              Developer tools are only available in the desktop application. Please use the desktop app to access admin features.
            </p>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Success Display */}
      {fixed && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-900">Data Fixed Successfully</p>
            <p className="text-sm text-green-700 mt-1">
              Updated: {fixed.updated.clients} clients, {fixed.updated.items} items, {fixed.updated.invoices} invoices
            </p>
          </div>
        </div>
      )}

      {/* Fix Orphaned Data */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">
              Fix Orphaned Data
            </h2>
            <p className="text-sm text-slate-600">
              Assign orphaned invoices, clients, and items to your current account
            </p>
          </div>
          <button
            onClick={fixOrphanedData}
            disabled={fixing}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {fixing ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Fixing...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                Fix Now
              </>
            )}
          </button>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700">
          <p className="font-medium mb-2">What this does:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Finds invoices, clients, and items without an account_id</li>
            <li>Assigns them to your current account</li>
            <li>Fixes "Invoice not found or access denied" errors</li>
            <li>Safe to run multiple times</li>
          </ul>
        </div>
      </div>

      {/* Database Stats */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">
            Database Statistics
          </h2>
          <button
            onClick={loadStats}
            className="px-3 py-1.5 text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <Database size={16} />
            Refresh
          </button>
        </div>

        {stats ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Global Counts
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Users:</span>
                  <span className="font-medium text-slate-900">{stats.users}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Accounts:</span>
                  <span className="font-medium text-slate-900">{stats.accounts}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Clients:</span>
                  <span className="font-medium text-slate-900">{stats.clients}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Items:</span>
                  <span className="font-medium text-slate-900">{stats.items}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Invoices:</span>
                  <span className="font-medium text-slate-900">{stats.invoices}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Your Account
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">User ID:</span>
                  <span className="font-medium text-slate-900">{stats.currentUser || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Account ID:</span>
                  <span className="font-medium text-slate-900">{stats.currentAccount || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">My Clients:</span>
                  <span className="font-medium text-slate-900">{stats.myClients || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">My Items:</span>
                  <span className="font-medium text-slate-900">{stats.myItems || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">My Invoices:</span>
                  <span className="font-medium text-slate-900">{stats.myInvoices || 0}</span>
                </div>
              </div>
            </div>

            <div className="col-span-2 bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-xs font-medium text-amber-900 uppercase tracking-wide mb-2">
                Orphaned Data (Needs Fixing)
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700">Clients:</span>
                  <span className={`font-medium ${stats.orphanedClients > 0 ? 'text-amber-900' : 'text-green-700'}`}>
                    {stats.orphanedClients}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700">Items:</span>
                  <span className={`font-medium ${stats.orphanedItems > 0 ? 'text-amber-900' : 'text-green-700'}`}>
                    {stats.orphanedItems}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700">Invoices:</span>
                  <span className={`font-medium ${stats.orphanedInvoices > 0 ? 'text-amber-900' : 'text-green-700'}`}>
                    {stats.orphanedInvoices}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-slate-500 mb-3">Click refresh to load statistics</p>
            <button
              onClick={loadStats}
              className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
            >
              Load Stats
            </button>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-900 mb-2">
          How to fix "Invoice not found or access denied" error:
        </p>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Click "Load Stats" to see if you have orphaned data</li>
          <li>If orphaned invoices/clients/items show &gt; 0, click "Fix Now"</li>
          <li>Go back to Invoices page and try again</li>
          <li>If issue persists, log out and log in again</li>
        </ol>
      </div>
    </div>
  );
}
